import React, { useState, useEffect } from 'react';
import { PUBLIC_FN_TOKEN } from '@/lib/publicFnToken';
import WhatsAppPopup from '@/components/layout/WhatsAppPopup';
import { useNavigate, Link } from 'react-router-dom';
import DateRangePicker from '@/components/search/DateRangePicker';
import TimeSelect from '@/components/search/TimeSelect';
import { MapPin, MessageCircle, ChevronDown, ChevronUp, Phone, Star, Shield, Clock, Truck, CheckCircle, Search, CreditCard, Lock, Calendar, Globe, Handshake } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useLanguage } from '@/lib/useLanguage';
// Below-the-fold, non-critical sections are lazy-loaded so they don't ship in
// the initial bundle — this frees the main thread and network for the hero/LCP.
const LandingReviewForm = React.lazy(() => import('@/components/reviews/LandingReviewForm'));
const WhyLocalSpecialist = React.lazy(() => import('@/components/landing/WhyLocalSpecialist'));
const RealReviewsSection = React.lazy(() => import('@/components/seo/RealReviewsSection'));
import { START_LANGS } from '@/components/landing/orthocareStartI18n';
import { getStartStringsForDomain, getStartFaqsForDomain } from '@/components/landing/orthocareStartDomainOverrides';
import ComplaintsBookButton from '@/components/layout/ComplaintsBookButton';
import HomeSchemas from '@/components/seo/HomeSchemas';
import HygieneGuaranteeSection from '@/components/seo/HygieneGuaranteeSection';
import QuoteConfigurator from '@/components/seo/QuoteConfigurator';
import { siteBaseUrl } from '@/lib/siteDomain';
import { getHeroImageForOrthocareDomain, getSiteImage, SITE_IMAGE_KEYS } from '@/lib/siteImages';
import CityAutocomplete from '@/components/landing/CityAutocomplete';

function FAQSection({ language }) {
  const [open, setOpen] = React.useState(null);
  const items = getStartFaqsForDomain(language);
  const S = getStartStringsForDomain(language);
  return (
    <section className="py-14 px-4" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%)' }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">FAQ</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{S.faqTitle}</h2>
        </div>
        <div className="space-y-2">
          {items.map((item, i) =>
          <div key={i} className={`border rounded-xl overflow-hidden transition-all duration-200 ${open === i ? 'border-blue-300 shadow-md' : 'border-gray-200 bg-white hover:border-blue-200'}`}>
              <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors">
                <span className="font-semibold text-gray-800 text-sm pr-4">{item.q}</span>
                {open === i ?
              <ChevronUp className="w-4 h-4 text-blue-600 shrink-0" /> :
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
              </button>
              {open === i &&
            <div className="px-5 pb-4 pt-0 bg-blue-50 border-t border-blue-100">
                  <p className="text-gray-700 text-sm leading-relaxed">{item.a}</p>
                </div>
            }
            </div>
          )}
        </div>
        <div className="text-center mt-6">
          <a href="/faq" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-semibold">
            {S.seeAllFaqs} →
          </a>
        </div>
      </div>
    </section>);

}

export default function OrthocarePresentationRent() {
  const navigate = useNavigate();
  const { lang: language, setLanguage } = useLanguage();
  const S = getStartStringsForDomain(language);

  const now = new Date();
  const isAfter22 = now.getHours() >= 22;
  const minStartDate = isAfter22 ?
  new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] :
  new Date().toISOString().split('T')[0];
  const defaultStartDate = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  // Default to a 1-week rental — the most common duration — so the first
  // price shown reflects a realistic (higher) ticket instead of a 1-day min.
  const defaultEndDate = new Date(new Date(defaultStartDate).getTime() + 7 * 86400000).toISOString().split('T')[0];

  const [deliveryHint, setDeliveryHint] = useState('');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [differentReturnCity, setDifferentReturnCity] = useState(false);
  const [formError, setFormError] = useState('');
  const [searchData, setSearchData] = useState({
    city: '',
    returnCity: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    pickupTime: '10:00',
    returnTime: '10:00'
  });

  // Single cached backend call instead of 2 direct entity reads per visitor.
  // getSearchPageData caches server-side for 2 min, so 1000 visitors = 1 DB
  // read instead of 2000. This is critical to stay under the read-volume limit.
  const { data: pageData, isLoading: settingsLoading } = useQuery({
    queryKey: ['search-page-data'],
    queryFn: async () => {
      const res = await base44.functions.invoke('getSearchPageData', {});
      return res.data;
    },
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });
  const settings = pageData?.allSettings || [];
  // Use ALL counties (concelhos + freguesias) for the city picker — the list
  // must match exactly what's configured in "Concelhos e Entregas" settings.
  const backendCounties = (pageData?.counties && pageData.counties.length > 0) ? pageData.counties : (pageData?.concelhos || []);
  // Fallback removed: it read 3000 counties for every visitor whenever the
  // shared payload was briefly empty, keeping the app over its entity
  // read-volume limit. getSearchPageData now always answers (last good snapshot).
  const counties = backendCounties;

  // Hero background per orthocare domain, overridable from /og-preview (Settings).
  // The resolved URL is cached in localStorage so repeat visits render the
  // image instantly (browser cache hit) instead of waiting for the Settings
  // fetch — only the very first visit shows the dark base until settings
  // resolve, after which the URL is stored and reused on every later load.
  const [heroImg, setHeroImg] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('heroImg_orthocare') || null;
  });
  useEffect(() => {
    if (settingsLoading) return;
    const resolved = getHeroImageForOrthocareDomain(settings);
    if (resolved && resolved !== heroImg) {
      setHeroImg(resolved);
      try { localStorage.setItem('heroImg_orthocare', resolved); } catch (e) {}
    }
  }, [settingsLoading, settings]);
  const [imgReady, setImgReady] = useState(true);
  useEffect(() => { setImgReady(true); }, [heroImg]);

  // Detecta a localidade do utilizador e pré-preenche a cidade se for portuguesa.
  useEffect(() => {
    if (!counties.length || searchData.city) return;
    let cancelled = false;
    const norm = (v) => (v || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');
    const matchCounty = (raw) => {
      if (!raw) return null;
      const key = norm(raw);
      let m = counties.find((c) => norm(c.name) === key);
      if (m) return m.name;
      m = counties.find((c) => norm(c.name).includes(key) || key.includes(norm(c.name)));
      return m ? m.name : null;
    };
    const applyFromAddress = (addr) => {
      if (cancelled) return;
      if (!addr || (addr.country_code && addr.country_code.toLowerCase() !== 'pt')) return;
      const raw = addr.county || addr.city || addr.town || addr.village || addr.municipality;
      const matched = matchCounty(raw);
      if (matched) setSearchData((s) => (s.city ? s : { ...s, city: matched }));
    };
    const reverseGeocode = async (lat, lon) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=pt`);
        const d = await r.json();
        if (d && d.address) applyFromAddress(d.address);
      } catch {}
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => reverseGeocode(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { timeout: 8000, maximumAge: 600000 }
      );
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counties.length]);

  useEffect(() => {
    const now = new Date();
    const startDateObj = new Date(searchData.startDate);
    const endDateObj = new Date(searchData.endDate);
    const isToday = startDateObj.toDateString() === now.toDateString();
    let newState = { ...searchData };
    let changed = false;
    if (isToday) {
      const minTime = new Date(now.getTime() + 60 * 60 * 1000);
      let nextH = minTime.getHours();
      let nextM = minTime.getMinutes() < 30 ? 30 : 0;
      if (minTime.getMinutes() >= 30) nextH = (nextH + 1) % 24;
      const minStr = `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
      if (searchData.pickupTime < minStr) {newState.pickupTime = minStr;changed = true;}
    }
    if (endDateObj < startDateObj) {newState.endDate = searchData.startDate;changed = true;}
    const isSameDay = startDateObj.toDateString() === endDateObj.toDateString();
    if (isSameDay && newState.returnTime < newState.pickupTime) {
      newState.returnTime = newState.pickupTime;
      changed = true;
    }
    if (changed) setSearchData(newState);
  }, [searchData.startDate, searchData.endDate, searchData.pickupTime]);

  const minPickupStr = (() => {
    const startDateObj = new Date(searchData.startDate);
    if (startDateObj.toDateString() !== new Date().toDateString()) return null;
    const minTime = new Date(Date.now() + 60 * 60 * 1000);
    let nextH = minTime.getHours();
    let nextM = minTime.getMinutes() < 30 ? 30 : 0;
    if (minTime.getMinutes() >= 30) nextH = (nextH + 1) % 24;
    return `${String(nextH).padStart(2, '0')}:${String(nextM).padStart(2, '0')}`;
  })();

  const isSameDayReturn = new Date(searchData.startDate).toDateString() === new Date(searchData.endDate).toDateString();
  const minReturnStr = isSameDayReturn ? searchData.pickupTime : null;

  const handleSearch = async () => {
    const searchSmsEnabled = settings.find((s) => s.setting_key === 'search_sms_enabled')?.setting_value !== 'false';
    if (searchSmsEnabled) {
      base44.functions.invoke('sendSearchSms', { fnToken: PUBLIC_FN_TOKEN, city: searchData.city, startDate: searchData.startDate, endDate: searchData.endDate, pickupTime: searchData.pickupTime, returnTime: searchData.returnTime, returnCity: searchData.returnCity, source: 'orthocare', language }).catch(() => {});
    }
    const params = new URLSearchParams({
      city: searchData.city,
      startDate: searchData.startDate,
      endDate: searchData.endDate,
      pickupTime: searchData.pickupTime,
      returnTime: searchData.returnTime
    });
    if (differentReturnCity && searchData.returnCity.trim()) params.set('returnCity', searchData.returnCity);
    if (deliveryHint) params.set('deliveryHint', deliveryHint);
    params.set('source', 'orthocare');
    sessionStorage.setItem('searchSource', 'orthocare');
    navigate(`/SearchResults?${params.toString()}`);
  };

  const testimonials = {
    pt: [
    { name: 'Maria Fernanda', city: 'Lisboa', text: 'Serviço excelente! A cadeira de rodas chegou no dia seguinte, em perfeito estado. Recomendo a toda a gente.', stars: 5 },
    { name: 'João Rodrigues', city: 'Porto', text: 'Precisei de uma cama articulada para a minha mãe após uma cirurgia e o processo foi simples e rápido. Muito obrigado!', stars: 5 },
    { name: 'Ana Costa', city: 'Braga', text: 'Preços justos, entrega pontual e equipamento em ótimo estado. Voltarei a utilizar sempre que precisar.', stars: 5 },
    { name: 'Carlos Mendes', city: 'Cascais', text: 'Atendimento ao cliente fantástico. Responderam a todas as minhas dúvidas via WhatsApp em minutos.', stars: 5 },
    { name: 'James Wilson', city: 'Londres, UK', text: 'I was visiting Lisbon for a month and needed a wheelchair. Orthocare delivered it to my hotel the next morning. Outstanding service!', stars: 5 },
    { name: 'Emily Johnson', city: 'Nova Iorque, EUA', text: 'Rented a hospital bed for my mother during our stay in Cascais. The team was incredibly helpful and professional.', stars: 5 }],
    en: [
    { name: 'Maria Fernanda', city: 'Lisbon', text: 'Excellent service! The wheelchair arrived the next day in perfect condition. I recommend it to everyone.', stars: 5 },
    { name: 'João Rodrigues', city: 'Porto', text: 'I needed an articulated bed for my mother after surgery and the process was simple and quick. Thank you!', stars: 5 },
    { name: 'Ana Costa', city: 'Braga', text: 'Fair prices, punctual delivery and equipment in great condition. I will use this service again whenever needed.', stars: 5 },
    { name: 'Carlos Mendes', city: 'Cascais', text: 'Fantastic customer service. They answered all my questions via WhatsApp within minutes.', stars: 5 },
    { name: 'James Wilson', city: 'London, UK', text: 'I was visiting Lisbon for a month and needed a wheelchair. Orthocare delivered it to my hotel the next morning. Outstanding service!', stars: 5 },
    { name: 'Emily Johnson', city: 'New York, USA', text: 'Rented a hospital bed for my mother during our stay in Cascais. The team was incredibly helpful and professional.', stars: 5 }]
  };

  const [testimonialIndex, setTestimonialIndex] = React.useState(0);
  const [reviewFormOpen, setReviewFormOpen] = useState(false);
  // Defer the (below-the-fold) reviews fetch until the browser is idle, so it
  // doesn't compete with the hero image download on slow connections.
  const [reviewsEnabled, setReviewsEnabled] = useState(false);
  React.useEffect(() => {
    let h;
    if (window.requestIdleCallback) {
      h = window.requestIdleCallback(() => setReviewsEnabled(true), { timeout: 3000 });
      return () => window.cancelIdleCallback(h);
    }
    h = setTimeout(() => setReviewsEnabled(true), 1500);
    return () => clearTimeout(h);
  }, []);

  const { data: approvedReviews = [] } = useQuery({
    queryKey: ['landing-approved-reviews'],
    queryFn: () => base44.entities.Review.filter({ status: "approved" }, "-created_date", 50),
    staleTime: 1000 * 60 * 5,
    enabled: reviewsEnabled,
  });

  const realTestimonials = (approvedReviews || []).map((r) => ({
    name: r.customer_name || (language === 'pt' ? 'Cliente' : 'Customer'),
    city: r.location || r.equipment_name || 'Portugal',
    text: r.review_text || '',
    stars: r.rating || 5,
    isReal: true,
  }));

  const currentTestimonials = realTestimonials.length > 0
    ? realTestimonials
    : (testimonials[language] || testimonials.en);

  const featuredCities = [
  { name: 'Lisboa', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/514b6bf41_images.jpeg' },
  { name: 'Porto', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/c84c2d466_17750779733243039880391257164712.jpg' },
  { name: 'Braga', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/a17ad0f78_17750780553527638477717099787799.jpg' },
  { name: 'Covilhã', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/603c6293d_1775078155913733176041155689769.jpg' },
  { name: 'Cascais', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/d0a7fd84b_17750781947918316063259178449606.jpg' },
  { name: 'Aveiro', image: 'https://media.base44.com/images/public/691220492173ae6cbe68ab07/4261228c6_17750782417964655631148216950851.jpg' }];

  const trustBadges = [
  { Icon: Truck, label: S.trustBadge1 || S.trust1 },
  { Icon: Shield, label: S.trustBadge2 || S.trust2 },
  { Icon: Clock, label: S.trustBadge3 || S.trust3 },
  { Icon: CheckCircle, label: S.trustBadge4 || (language === 'pt' ? 'Sem compromisso' : 'No commitment') }];

  const steps = [
  { step: '01', icon: Search, title: S.step1Title, desc: S.step1Desc },
  { step: '02', icon: CreditCard, title: S.step2Title, desc: S.step2Desc },
  { step: '03', icon: Truck, title: S.step3Title, desc: S.step3Desc }];

  const currentLangDef = START_LANGS.find((l) => l.code === language) || START_LANGS[0];

  React.useEffect(() => {
    const siteBase = siteBaseUrl();
    // Canonical homepage is the domain root "/" (not /start) so Google indexes
    // the clean domain URL. The /start route still exists but canonicalizes here.
    // IMPORTANT: keep the trailing slash consistent with the inline index.html
    // script (which sets canonical to origin+pathname = "https://orthocare.pt/")
    // so Google never sees two different canonical URLs for the same page.
    const ptUrl = `${siteBase}/`;
    const langUrl = (l) => l === 'pt' ? ptUrl : `${siteBase}/?lang=${l}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {canonical = document.createElement('link');canonical.rel = 'canonical';document.head.appendChild(canonical);}
    canonical.href = langUrl(language);
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    // All 7 languages declared so Google associates PT/EN/ES/FR/DE/IT/NL
    // versions correctly and stops ranking EN/ES pages for PT searches.
    [
      { hreflang: 'pt-PT', href: ptUrl },
      { hreflang: 'pt', href: ptUrl },
      { hreflang: 'en', href: langUrl('en') },
      { hreflang: 'en-GB', href: langUrl('en') },
      { hreflang: 'en-US', href: langUrl('en') },
      { hreflang: 'en-IE', href: langUrl('en') },
      { hreflang: 'es', href: langUrl('es') },
      { hreflang: 'es-ES', href: langUrl('es') },
      { hreflang: 'fr', href: langUrl('fr') },
      { hreflang: 'fr-FR', href: langUrl('fr') },
      { hreflang: 'fr-BE', href: langUrl('fr') },
      { hreflang: 'de', href: langUrl('de') },
      { hreflang: 'de-DE', href: langUrl('de') },
      { hreflang: 'de-AT', href: langUrl('de') },
      { hreflang: 'de-CH', href: langUrl('de') },
      { hreflang: 'it', href: langUrl('it') },
      { hreflang: 'it-IT', href: langUrl('it') },
      { hreflang: 'nl', href: langUrl('nl') },
      { hreflang: 'nl-NL', href: langUrl('nl') },
      { hreflang: 'nl-BE', href: langUrl('nl') },
      { hreflang: 'x-default', href: ptUrl },
    ].forEach(({ hreflang, href }) => {
      const l = document.createElement('link');
      l.rel = 'alternate'; l.hreflang = hreflang; l.href = href;
      l.setAttribute('data-start-hreflang', '1');
      document.head.appendChild(l);
    });
    document.title = S.titleMeta;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', S.descMeta);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', document.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', S.descMeta);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', langUrl(language));
    let ogImgEl = document.querySelector('meta[property="og:image"]');
    if (!ogImgEl) { ogImgEl = document.createElement('meta'); ogImgEl.setAttribute('property', 'og:image'); document.head.appendChild(ogImgEl); }
    ogImgEl.setAttribute('content', getSiteImage(settings, SITE_IMAGE_KEYS.ogOrthocare));
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'breadcrumb-start-ld';
    breadcrumbScript.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Início", "item": `${siteBase}/` }, { "@type": "ListItem", "position": 2, "name": "Aluguer Equipamento", "item": ptUrl }] });
    document.head.appendChild(breadcrumbScript);
    document.documentElement.lang = language;
    return () => {
      if (canonical) canonical.href = ptUrl;
      // FIX: cleanup must remove the tags this effect created (data-start-hreflang),
      // not data-page-hreflang (those belong to EquipmentSEOPage etc).  Without
      // this fix, stale homepage hreflang tags persisted when navigating away,
      // creating duplicate/conflicting hreflang signals across pages.
      document.querySelectorAll('link[rel="alternate"][data-start-hreflang]').forEach((n) => n.remove());
      document.getElementById('breadcrumb-start-ld')?.remove();
    };
  }, [language, settings]);

  return (
    <div className="min-h-screen bg-white">
      <HomeSchemas />
      {/* ── Header (contains National Coverage Banner + Logo/Language) ── */}
      <header className="absolute top-0 left-0 right-0 z-50">
        {/* National Coverage Banner (BLUF) — hidden per request */}
        {false && (
        <div className="w-full bg-gradient-to-r from-[#0a3d62] to-[#0e4d78] text-white text-center py-2 px-4 text-xs sm:text-sm font-semibold">
          {language === 'pt'
            ? '🇵🇹 Entrega e Recolha de Equipamento de Mobilidade em Todos os Distritos de Portugal Continental — Hotéis, Airbnbs, Residências e Aeroportos'
            : '🇵🇹 Nationwide Mobility Equipment Delivery & Pickup Across All Mainland Portugal Districts — Hotels, Airbnbs, Residences & Airports'}
        </div>
        )}
        <div className="bg-gradient-to-b from-black/30 to-transparent">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-1 flex items-center justify-between max-w-7xl gap-4">
          <Link to="/start" aria-label="Orthocare Rent" className="inline-flex items-center rounded-lg">
            <img
              src="https://media.base44.com/images/public/691220492173ae6cbe68ab07/eec4f6188_file_000000000f3871f4acdc11aca9b1a76e.png"
              alt="Orthocare Rent" width={220} height={80} className="h-16 sm:h-20 max-w-[180px] sm:max-w-[220px] w-auto object-contain rounded-lg drop-shadow-xl" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3 relative">
            <button
              onClick={() => { setFormError(''); document.getElementById('search-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#cebe75] hover:brightness-110 text-[#0a3d62] text-xs font-bold transition-all shadow-lg min-h-[44px]">
              <Calendar className="w-4 h-4" />
              {language === 'pt' ? 'Reservar' : 'Book Now'}
            </button>
            <button
              onClick={() => { setFormError(''); document.getElementById('search-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              aria-label={language === 'pt' ? 'Reservar' : 'Book Now'}
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#cebe75] hover:brightness-110 text-[#0a3d62] transition-all shadow-lg shrink-0">
              <Calendar className="w-5 h-5" />
            </button>
            <button
              onClick={() => setLangMenuOpen((v) => !v)}
              className="px-2.5 py-2 rounded-lg bg-white/15 backdrop-blur-md hover:bg-white/25 text-white text-xs font-bold flex items-center gap-1.5 transition-colors border border-white/20 min-h-[44px]">
              <img src={currentLangDef.flag} alt={currentLangDef.code} className="h-3.5 w-auto rounded-sm" />
              {currentLangDef.code.toUpperCase()}
              <ChevronDown className="w-3 h-3" />
            </button>
            {langMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                {START_LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLanguage(l.code); setLangMenuOpen(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors ${l.code === language ? 'text-[#0a3452] bg-gray-50' : 'text-gray-700'}`}>
                    <img src={l.flag} alt={l.code} className="h-4 w-auto rounded-sm" />
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      </header>

      {/* ── Hero + Search card ──────────────────────────────────── */}
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
        .hero-fade-1 { animation: fadeInUp 0.7s ease both; }
        .hero-fade-2 { animation: fadeInUp 0.7s ease 0.15s both; }
        .hero-fade-3 { animation: fadeInUp 0.7s ease 0.3s both; }
        .hero-fade-4 { animation: fadeInUp 0.7s ease 0.45s both; }
        .city-card-overlay { transition: all 0.4s ease; }
        .city-card:hover .city-card-overlay { background: linear-gradient(to top, rgba(10,61,98,0.85) 0%, rgba(10,61,98,0.3) 60%, transparent 100%) !important; }
        .city-card:hover .city-card-name { transform: translateY(-4px); letter-spacing: 0.05em; }
        .city-card-name { transition: transform 0.3s ease, letter-spacing 0.3s ease; }
        .m4r-field { border: 1.5px solid #0a3d62; border-radius: 10px; }
        .m4r-field:focus-within { box-shadow: 0 0 0 3px rgba(10,52,82,0.25); border-color: #0a3d62; }
      `}</style>
      <div className="relative overflow-hidden" style={{ minHeight: '640px', background: '#0a3d62' }}>
        {heroImg && (
          <img
            src={heroImg}
            alt={S.heroImgAlt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '65% center', opacity: imgReady ? 1 : 0, transition: 'opacity 0.6s ease' }}
            fetchPriority="high" loading="eager" decoding="sync"
            onLoad={() => setImgReady(true)} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,61,98,0.82) 0%, rgba(10,61,98,0.45) 50%, rgba(0,0,0,0.35) 100%)' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pt-28 sm:pt-36 pb-24 sm:pb-10 flex flex-col items-center text-center">
          <span className="hero-fade-1 inline-flex items-center gap-2 text-[#cebe75] text-xs font-bold tracking-[0.2em] uppercase mb-4">
            <span className="w-6 h-px bg-[#cebe75]" />{S.badge}<span className="w-6 h-px bg-[#cebe75]" />
          </span>
          <h1 className="hero-fade-2 text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5 text-white drop-shadow-xl max-w-3xl">
            {S.h1a} <span className="text-[#cebe75]">{S.h1b}</span>&nbsp;<span className="text-[#cebe75]">{S.h1c}</span>
          </h1>
          <p className="hero-fade-3 text-white/90 text-sm sm:text-base mb-6 max-w-2xl">
            {S.heroP}
          </p>
          <div className="hero-fade-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8">
            {[
            { Icon: Truck, label: S.trust1 },
            { Icon: MessageCircle, label: S.trust2 },
            { Icon: CheckCircle, label: S.trust3 }].
            map(({ Icon, label }) =>
            <div key={label} className="flex items-center gap-2 text-white/95 text-xs sm:text-sm font-medium">
                <Icon className="w-4 h-4 shrink-0 text-[#cebe75]" />
                {label}
              </div>
            )}
          </div>

          {/* ── Booking card ───────────────────────────────────── */}
          <div className="w-full max-w-2xl bg-white/95 rounded-3xl shadow-2xl ring-1 ring-black/5 overflow-hidden" id="search-section">
            <div className="bg-gradient-to-r from-[#0a3d62] to-[#082f4d] px-5 sm:px-7 py-3 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Search className="w-4 h-4 text-white" />
              </div>
              <p className="text-white text-sm font-bold leading-tight">{S.findEquip}</p>
            </div>

            <div className="p-5 sm:p-7 space-y-4">
              {/* CITY */}
              <div>
                <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[#0a3d62] tracking-wider mb-1.5">
                  <MapPin className="w-3 h-3" />{S.cityLabel}
                </label>
                <div className="m4r-field relative flex items-center bg-gray-50/50 px-3.5 transition-all hover:bg-white hover:shadow-sm">
                  <MapPin className="w-4 h-4 text-[#0a3d62] shrink-0" />
                  <CityAutocomplete
                    value={searchData.city}
                    onChange={(v) => { setSearchData({ ...searchData, city: v }); if (deliveryHint && v !== 'Lisboa') setDeliveryHint(''); if (formError) setFormError(''); }}
                    placeholder={S.whereAreYouGoing}
                    counties={counties}
                    inputClassName="w-full px-2.5 py-2.5 text-sm text-[#0a3d62] placeholder-gray-400 focus:outline-none bg-transparent font-medium" />
                  {searchData.city &&
                  <button type="button" onClick={() => setSearchData({ ...searchData, city: '' })} className="text-gray-400 hover:text-[#0a3d62] shrink-0 transition-colors">
                      <span className="text-lg leading-none">×</span>
                    </button>
                  }
                </div>
              </div>

              {/* Delivery in another city (return city differs) */}
              <label className="flex items-center gap-2.5 cursor-pointer select-none group">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={differentReturnCity}
                  aria-label={S.returnAnother}
                  onClick={() => {
                    setDifferentReturnCity((v) => {
                      const nv = !v;
                      if (!nv) setSearchData((s) => ({ ...s, returnCity: '' }));
                      return nv;
                    });
                  }}
                  className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${differentReturnCity ? 'bg-[#0a3d62]' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${differentReturnCity ? 'translate-x-4' : ''}`} />
                </button>
                <span className="text-[11px] sm:text-xs font-semibold text-[#0a3d62] tracking-wide">
                  {S.returnAnother}
                </span>
              </label>

              {differentReturnCity && (
                <div>
                  <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[#0a3d62] tracking-wider mb-1.5">
                    <MapPin className="w-3 h-3" />{S.returnCityLabel}
                  </label>
                  <div className="m4r-field relative flex items-center bg-gray-50/50 px-3.5 transition-all hover:bg-white hover:shadow-sm">
                    <MapPin className="w-4 h-4 text-[#0a3d62] shrink-0" />
                    <CityAutocomplete
                      value={searchData.returnCity}
                      onChange={(v) => setSearchData({ ...searchData, returnCity: v })}
                      placeholder={S.whereReturn}
                      counties={counties}
                      inputClassName="w-full px-2.5 py-2.5 text-sm text-[#0a3d62] placeholder-gray-400 focus:outline-none bg-transparent font-medium" />
                    {searchData.returnCity &&
                      <button type="button" onClick={() => setSearchData({ ...searchData, returnCity: '' })} className="text-gray-400 hover:text-[#0a3d62] shrink-0 transition-colors">
                        <span className="text-lg leading-none">×</span>
                      </button>
                    }
                  </div>
                </div>
              )}

              {/* DATES */}
              <div>
                <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[#0a3d62] tracking-wider mb-1.5">
                  <Calendar className="w-3 h-3" />{S.datesLabel}
                </label>
                <div className="m4r-field bg-gray-50/50 [&_button]:w-full [&_button]:py-2.5 [&_button]:text-sm [&_button]:text-[#0a3d62] [&_button]:font-medium [&_button]:focus:outline-none [&_button]:px-3.5 [&_button]:flex [&_button]:items-center [&_button]:gap-2 transition-all hover:bg-white hover:shadow-sm">
                  <DateRangePicker
                    startDate={searchData.startDate}
                    endDate={searchData.endDate}
                    minDate={minStartDate}
                    lang={language || 'pt'}
                    onChange={({ startDate, endDate }) => setSearchData((s) => ({ ...s, startDate, endDate }))} />
                </div>
              </div>

              {/* START / END */}
              <div className="grid grid-cols-2 gap-3">
              {[['pickupTime', S.startLabel], ['returnTime', S.endLabel]].map(([key, label]) =>
                <div key={key}>
                  <label className="flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-[#0a3d62] tracking-wider mb-1.5">
                    <Clock className="w-3 h-3" />{label}
                  </label>
                  <TimeSelect
                     value={searchData[key]}
                     onChange={(v) => setSearchData(prev => ({ ...prev, [key]: v }))}
                     minTime={key === 'pickupTime' ? minPickupStr : minReturnStr}
                    date={key === 'pickupTime' ? searchData.startDate : searchData.endDate}
                    language={language}
                    iconColor="#0a3d62"
                    triggerClassName="m4r-field bg-gray-50/50 hover:bg-white hover:shadow-sm transition-all text-sm text-[#0a3d62]"
                  />
                </div>
                )}
              </div>

              {/* SEARCH button */}
              <button
                type="button"
                onClick={() => {if (!searchData.city.trim()) {setFormError(S.pleaseEnterCity);return;}if (differentReturnCity && !searchData.returnCity.trim()) {setFormError(S.pleaseEnterReturnCity);return;}setFormError('');handleSearch();}}
                className="group w-full mt-1 bg-gradient-to-r from-[#0a3d62] to-[#082f4d] hover:from-[#082f4d] hover:to-[#061f35] text-white font-bold py-4 rounded-2xl text-sm sm:text-base transition-all cursor-pointer shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2.5 uppercase tracking-[0.15em] relative overflow-hidden">
                <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
                <Search className="w-5 h-5 relative z-10" />
                <span className="relative z-10">{S.searchBtn}</span>
              </button>

              {formError && (
                <div role="alert" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5 text-red-700 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                  {formError}
                </div>
              )}

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 pt-1">
                {[
                  { Icon: CheckCircle, label: S.freeCancel },
                  { Icon: Lock, label: S.securePay }
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex items-center gap-1 text-gray-600 text-[10px] font-medium">
                    <Icon className="w-3 h-3 text-[#0a3d62]" />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom contact nav ──────────────────────────────────── */}
      <nav className="sticky bottom-0 z-40 bg-[#0a3d62] border-t border-white/10">
        <div className="max-w-3xl mx-auto grid grid-cols-3 divide-x divide-white/10">
          <a href="#" onClick={(e) => e.preventDefault()} className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <MessageCircle className="w-4 h-4 text-[#cebe75]" />
            <span className="text-xs font-bold">{S.liveChat}</span>
          </a>
          <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <MessageCircle className="w-4 h-4 text-[#cebe75]" />
            <span className="text-xs font-bold">WhatsApp</span>
          </a>
          <a href="tel:+351216035281" className="flex items-center justify-center gap-2 py-3 text-white hover:bg-white/10 transition-colors">
            <Phone className="w-4 h-4 text-[#cebe75]" />
            <span className="text-xs font-bold">{S.call}</span>
          </a>
        </div>
      </nav>

      {/* ── Tourist Banner ─────────────────────────────────────── */}
      {language !== 'pt' &&
      <div className="bg-amber-50 border-b border-amber-200 py-3 px-4">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🌍</span>
              <div>
                <p className="font-bold text-amber-900 text-sm">{S.touristTitle}</p>
                <p className="text-amber-700 text-xs">{S.touristDesc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-amber-700 font-medium">🇬🇧 🇺🇸 🇫🇷 🇩🇪 🇮🇹 🇪🇸 🇳🇱 🇧🇷</span>
            </div>
          </div>
        </div>
      }

      {/* ── Stats bar ──────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #0a3452, #061f35)' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-white/20">
          {[
          { val: '4.9/5', label: S.googleRating, sub: '★', icon: Star },
          { val: '2.000+', label: S.customers, icon: CheckCircle },
          { val: '60+', label: S.cities, icon: MapPin },
          { val: '100%', label: S.securePaymentStat, icon: Lock }].
          map(({ val, label, icon: Icon, sub }) =>
          <div key={label} className="py-5 text-center px-2">
              {Icon && <Icon className="w-4 h-4 text-white/40 mx-auto mb-1" />}
              <div className="text-xl sm:text-2xl font-black text-white flex items-center justify-center gap-1">
                {sub === '★' && <Star className="w-4 h-4 fill-amber-400 text-amber-400" />}
                {val}
              </div>
              <p className="text-[11px] text-white/70 mt-0.5 font-medium">{label}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Cities grid ────────────────────────────────────────── */}
      <section className="py-12 px-4 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">{S.coverageLabel}</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{S.bestSellers}</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {featuredCities.map((city) =>
            <div key={city.name}
            className="city-card relative cursor-pointer overflow-hidden rounded-xl shadow-md h-28 sm:h-40 group"
            onClick={() => {setSearchData((s) => ({ ...s, city: city.name }));if (city.name !== 'Lisboa') setDeliveryHint('');window.scrollTo({ top: 0, behavior: 'smooth' });}}>
                <img src={city.image} alt={`${S.bestSellers} ${city.name}`} width={294} height={196} sizes="(max-width: 640px) 30vw, 160px" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" decoding="async" />
                <div className="city-card-overlay absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-3 px-2">
                  <h3 className="city-card-name text-xs sm:text-sm font-bold text-white drop-shadow text-center">{city.name}</h3>
                  <span className="mt-1 text-white/0 group-hover:text-white/90 text-[9px] sm:text-[10px] font-semibold tracking-wide transition-all duration-300 uppercase">
                    {S.seeEquipment}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────── */}
      <section className="py-12 px-4" style={{ background: 'linear-gradient(135deg, #eff6ff, #f0f9ff)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">{S.howTitle}</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{S.howSub}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 relative">
            <div className="hidden sm:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200" />
            {steps.map((s) => {
              const StepIcon = s.icon;
              return (
                <div key={s.step} className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100 text-center relative z-10">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0a3452] to-[#061f35] text-white flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <StepIcon className="w-6 h-6" />
                  </div>
                  <div className="text-xs font-black text-blue-600 mb-1 tracking-wider">{s.step}</div>
                  <h3 className="font-bold text-gray-900 mb-2 text-sm">{s.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
                </div>);
            })}
          </div>
        </div>
      </section>

      {/* ── Equipment Links (SEO Internal Linking) ──────────── */}
      <section className="py-12 bg-slate-50 px-4 border-t border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">
              {S.ourEquipment}
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {S.rentalEquipAvail}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { slug: 'cadeira-de-rodas', name: S.eqWheelchair, price: '5€' },
              { slug: 'cadeira-de-rodas-eletrica', name: S.eqElectric, price: '25€' },
              { slug: 'cama-articulada', name: S.eqBed, price: '15€' },
              { slug: 'andarilho', name: S.eqWalker, price: '3€' },
              { slug: 'scooter-mobilidade', name: S.eqScooter, price: '25€' },
            ].map((eq) => (
              <a key={eq.slug} href={`/aluguer/${eq.slug}`} className="bg-white hover:bg-blue-50 border border-slate-200 rounded-xl p-4 text-center transition group">
                <p className="text-sm font-semibold text-slate-800 group-hover:text-[#0a3452]">{eq.name}</p>
                <p className="text-xs text-slate-500 mt-1">{S.fromWord} {eq.price}/{S.dayWord}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hub Pages (SEO Internal Linking to new landing pages) ─── */}
      <section className="py-10 bg-white px-4 border-t border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
              {language === 'pt' ? 'Páginas de Aluguer e Lojas' : 'Rental Hub Pages & Stores'}
            </h2>
            <p className="text-sm text-gray-500">
              {language === 'pt' ? 'Acesso rápido às nossas páginas dedicadas' : 'Quick access to our dedicated pages'}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[
              { to: '/aluguer-cadeira-de-rodas', label: language === 'pt' ? 'Aluguer Cadeira de Rodas' : 'Wheelchair Rental' },
              { to: '/aluguer-cama-articulada', label: language === 'pt' ? 'Aluguer Cama Articulada' : 'Hospital Bed Rental' },
              { to: '/ortopedia-benfica', label: language === 'pt' ? 'Ortopedia Benfica' : 'Benfica Store' },
              { to: '/ortopedia-restelo', label: language === 'pt' ? 'Ortopedia Restelo' : 'Restelo Store' },
              { to: '/ortopedia-lisboa', label: language === 'pt' ? 'Ortopedia Lisboa' : 'Lisbon Orthopedics' },
              { to: '/aluguer-material-ortopedico-lisboa', label: language === 'pt' ? 'Aluguer Material Ortopédico Lisboa' : 'Orthopedic Equipment Rental Lisbon' },
              { to: '/lojas', label: language === 'pt' ? 'As Nossas Lojas' : 'Our Stores' },
              { to: '/guia', label: language === 'pt' ? 'Guia de Aluguer' : 'Rental Guide' },
            ].map((hub) => (
              <Link key={hub.to} to={hub.to} className="bg-slate-50 hover:bg-blue-50 border border-slate-200 rounded-xl p-3 text-center transition group">
                <p className="text-xs font-semibold text-slate-700 group-hover:text-[#0a3452]">{hub.label}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why local specialist (competitive positioning) ─────── */}
      <React.Suspense fallback={null}><WhyLocalSpecialist language={language} /></React.Suspense>

      {/* ── Real verified customer reviews ── */}
      <React.Suspense fallback={null}><RealReviewsSection max={6} /></React.Suspense>

      {/* ── Testimonials ───────────────────────────────────────── */}
      <section className="py-12 bg-white px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block bg-yellow-100 text-yellow-700 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">{S.reviewsLabel}</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{S.reviewsTitle}</h2>
            <div className="inline-flex items-center gap-2.5 mt-3 bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <span className="text-sm font-bold text-gray-900">4.9</span>
              <span className="w-px h-4 bg-gray-200" />
              <span className="text-xs text-gray-600 font-medium">Google Reviews</span>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#0a3452]/5 to-blue-50 rounded-2xl p-6 sm:p-8 shadow-md border border-[#0a3452]/10 flex flex-col min-h-[160px]">
            <div className="flex mb-4">
              {[...Array(currentTestimonials[testimonialIndex].stars)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
              {[...Array(5 - currentTestimonials[testimonialIndex].stars)].map((_, i) => <Star key={i} className="w-5 h-5 text-gray-200 fill-gray-200" />)}
            </div>
            <p className="text-gray-700 text-sm sm:text-base italic leading-relaxed flex-1">
              "{currentTestimonials[testimonialIndex].text}"
            </p>
            <div className="mt-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md" style={{ background: 'linear-gradient(135deg, #0a3452, #061f35)' }}>
                {currentTestimonials[testimonialIndex].name[0]}
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{currentTestimonials[testimonialIndex].name}</p>
                <p className="text-xs text-gray-600 flex items-center gap-1"><MapPin className="w-3 h-3" />{currentTestimonials[testimonialIndex].city}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-3 mt-5">
            <button onClick={() => setTestimonialIndex((i) => (i - 1 + currentTestimonials.length) % currentTestimonials.length)}
            className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0a3452] hover:text-[#0a3452] transition text-lg font-bold shrink-0">‹</button>
            <div className="flex gap-1 max-w-[160px] overflow-hidden">
              {currentTestimonials.map((_, i) =>
              <button key={i} onClick={() => setTestimonialIndex(i)} aria-label={`${S.testimonialDot || 'Testemunho'} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === testimonialIndex ? 'w-4 bg-[#0a3452]' : 'w-1.5 bg-gray-300'}`} />
              )}
            </div>
            <button onClick={() => setTestimonialIndex((i) => (i + 1) % currentTestimonials.length)}
            className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#0a3452] hover:text-[#0a3452] transition text-lg font-bold shrink-0">›</button>
          </div>

          <div className="flex justify-center mt-5">
            <button
              onClick={() => setReviewFormOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-[#0a3452]/30 text-[#0a3452] hover:bg-[#0a3452] hover:text-white text-sm font-semibold transition-all shadow-sm">
              <Star className="w-4 h-4" />
              {S.leaveReview}
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <FAQSection language={language} />

      <React.Suspense fallback={null}><LandingReviewForm open={reviewFormOpen} onClose={() => setReviewFormOpen(false)} language={language} /></React.Suspense>

      {/* ── CTA Banner ──────────────────────────────────────────── */}
      <QuoteConfigurator lang={language || 'pt'} />

      <HygieneGuaranteeSection lang={language || 'pt'} />

      <section className="py-12 px-4 text-white text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a3452 0%, #061f35 60%, #041a2d 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative z-10 max-w-lg mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-2">{S.readyToRent}</h2>
          <p className="text-white/80 text-sm sm:text-base mb-6">{S.startJourney}</p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-block bg-white text-[#0a3452] font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm">
            {S.searchEquip}
          </button>
        </div>
      </section>

      {/* ── Contact ─────────────────────────────────────────────── */}
      <section className="bg-gray-950 py-8 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-3">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            {S.support24}
          </span>
          <p className="text-gray-400 text-xs mb-5">{S.available24}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-sm mx-auto">
            <a href="tel:+351216035281" className="flex-1 bg-gray-800 hover:bg-gray-700 transition rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-white" /></div>
              <div className="text-left">
                <p className="text-gray-400 text-xs">{S.callNow}</p>
                <p className="text-white font-bold text-sm">+351 216 035 281</p>
              </div>
            </a>
            <a href="https://wa.me/351216035281" target="_blank" rel="noopener noreferrer"
            className="flex-1 bg-blue-600 hover:bg-blue-700 transition rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-white" /></div>
              <div className="text-left">
                <p className="text-white/70 text-xs">WhatsApp 24h</p>
                <p className="text-white font-bold text-sm">{S.sendMsg}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-300 py-8 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 mb-6">
          <div className="col-span-2 sm:col-span-1 min-w-0 overflow-hidden">
            <img src="https://media.base44.com/images/public/691220492173ae6cbe68ab07/eec4f6188_file_000000000f3871f4acdc11aca9b1a76e.png" alt="Orthocare" width={220} height={80} loading="lazy" className="h-12 sm:h-14 max-w-[120px] sm:max-w-[130px] w-auto object-contain rounded-lg drop-shadow-xl mb-2" />
            <p className="text-xs text-gray-400">{S.leadingOrtho}</p>
            <a href="/faq" className="mt-2 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.faqLink}
            </a>
            <a href="/about" className="mt-1 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.aboutLink}
            </a>
            <a href="/contact" className="mt-1 inline-block text-[#cebe75] text-xs hover:opacity-80 transition-colors">
              {S.contactLink}
            </a>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.services}</h3>
            <ul className="space-y-1 text-xs text-gray-400"><li>{S.equipmentRental}</li><li>{S.deliveryPickup}</li><li>{S.support}</li></ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.legal}</h3>
            <ul className="space-y-1 text-xs text-gray-400">
              <li><a href="/privacy" className="hover:text-white transition-colors">{S.privacy}</a></li>
              <li><a href="/terms" className="hover:text-white transition-colors">{S.terms}</a></li>
              <li><a href="/cookies" className="hover:text-white transition-colors">{S.cookies}</a></li>
            </ul>
            <div className="mt-3">
              <ComplaintsBookButton height={40} />
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold mb-2 text-sm">{S.contact}</h3>
            <p className="text-xs text-gray-400">geral@gestosageis.com</p>
            <p className="text-xs text-gray-400 mt-1">+351 216 035 281</p>
            <Link
              to="/parceiros"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#cebe75] text-[#0a3d62] text-xs font-bold hover:brightness-110 transition shadow-sm">
              <Handshake className="w-3.5 h-3.5" />
              Programa de Parceiros
            </Link>
            <div className="flex gap-2 mt-3">
              <a href="https://www.facebook.com/orthocare.rent" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="Facebook">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/orthocare_rent" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="Instagram">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/orthocare-rent" target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition" aria-label="LinkedIn">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"/></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-4 text-center text-xs text-gray-500">
          {S.copyright}
          <p className="mt-1">{S.brandOwned}</p>
        </div>
      </footer>

      <WhatsAppPopup lang={language || 'pt'} />
    </div>);

}